import {
  HomeIcon,
  UsersIcon,
  CalendarIcon,
  CreditCardIcon,
  ArchiveBoxIcon,
  BookOpenIcon,
  UserIcon,
  PlusIcon,
  Cog6ToothIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from "@heroicons/react/24/solid";
import { Header } from "@ui/components/Header";
import { useCallback, useEffect, useState } from "react";
import { getSupabaseClient } from '../lib/supabase';
import { useNavigate } from 'react-router-dom';
import { identifyGuide } from '../chameleon';
import HubPage from '../pages/HubPage';
import PackagesPage from '../pages/PackagesPage';
import ResourcesPage from '../pages/ResourcesPage';
import PaymentsPage from '../pages/PaymentsPage';
import SeekersPage from '../pages/SeekersPage';
import SchedulePage from '../pages/SchedulePage';
import ProfilePage from '../pages/ProfilePage';
import SettingsPage from '../pages/SettingsPage';
import ShareInviteLinkModal from '../components/ShareInviteLinkModal';
import RequestOffPlatformReviewModal from '../components/RequestOffPlatformReviewModal';
import SeekerDetailModal from '../components/SeekerDetailModal';
import ChatDrawer from '../components/ChatDrawer';

// Validate session helper - can be called anytime to check auth
async function validateSession(navigate: ReturnType<typeof useNavigate>): Promise<boolean> {
  try {
    const supabase = getSupabaseClient();

    // Get current session and validate it
    const { data: { session }, error: sessionError } = await supabase.auth.getSession();

    // If no session or session error, redirect to login
    if (sessionError || !session) {
      console.error('Session error or no session:', sessionError);
      navigate('/login');
      return false;
    }

    // Validate the session is not expired
    const now = Math.floor(Date.now() / 1000);
    if (session.expires_at && session.expires_at < now) {
      console.error('Session expired');
      await supabase.auth.signOut();
      navigate('/login');
      return false;
    }

    // Get current user to double-check validity
    const { data: { user }, error: authError } = await supabase.auth.getUser();
    if (authError || !user) {
      console.error('Auth error:', authError);
      // Session exists but user fetch failed - invalid session
      await supabase.auth.signOut();
      navigate('/login');
      return false;
    }

    return true;
  } catch (error) {
    console.error('Error validating session:', error);
    navigate('/login');
    return false;
  }
}

// Custom hook for user profile
function useUserProfile() {
  const navigate = useNavigate();
  const [userProfile, setUserProfile] = useState<{
    name?: string;
    role?: string;
    avatar?: string;
  }>({});
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  const revalidateSession = useCallback(async () => {
    const isValid = await validateSession(navigate);
    if (!isValid) {
      setIsAuthenticated(false);
    }
    return isValid;
  }, [navigate]);

  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        const isValid = await validateSession(navigate);
        if (!isValid) {
          setLoading(false);
          return;
        }

        setIsAuthenticated(true);

        const supabase = getSupabaseClient();
        const { data: { user } } = await supabase.auth.getUser();

        if (!user) {
          setLoading(false);
          return;
        }

        // Fetch profile data including terms_accepted
        const { data: profile, error: profileError } = await supabase
          .from('profiles')
          .select('full_name, terms_accepted')
          .eq('id', user.id)
          .single();

        if (profileError) {
          console.error('Profile fetch error:', profileError);
        } else if (profile) {
          // Check if terms are accepted
          if (!profile.terms_accepted) {
            navigate('/terms');
            return;
          }

          // Check if guide has active status
          const { data: guide, error: guideError } = await supabase
            .from('guides')
            .select('status')
            .eq('user_id', user.id)
            .single();

          if (guideError) {
            console.error('Guide status fetch error:', guideError);
          }

          // If guide doesn't have active status, redirect to onboarding
          if (!guide || guide.status !== 'active') {
            navigate('/onboarding');
            return;
          }

          setUserProfile({
            name: profile.full_name || 'Guide',
            role: 'Guide'
          });
        }
      } catch (error) {
        console.error('Error fetching user profile:', error);
        // On any unexpected error, redirect to login for safety
        navigate('/login');
      } finally {
        setLoading(false);
      }
    };

    fetchUserProfile();

    // Also listen for auth state changes
    const supabase = getSupabaseClient();
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_OUT' || (event === 'TOKEN_REFRESHED' && !session)) {
        setIsAuthenticated(false);
        navigate('/login');
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [navigate]);

  return { userProfile, loading, isAuthenticated, revalidateSession };
}

export default function Dashboard() {
  const navigate = useNavigate();
  const { userProfile, loading: authLoading, isAuthenticated, revalidateSession } = useUserProfile();
  const [activeTab, setActiveTab] = useState('hub');
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [isRequestReviewModalOpen, setIsRequestReviewModalOpen] = useState(false);
  const [isSeekerDetailModalOpen, setIsSeekerDetailModalOpen] = useState(false);
  const [isChatDrawerOpen, setIsChatDrawerOpen] = useState(false);
  const [chatSeekerId, setChatSeekerId] = useState<string | null>(null);
  const [selectedSeeker, setSelectedSeeker] = useState<any>(null);
  const [contentRefreshKey, setContentRefreshKey] = useState(0);
  const [shareLink, setShareLink] = useState<string | null>(null);
  const [shareLinkLoading, setShareLinkLoading] = useState(false);
  const [shareLinkError, setShareLinkError] = useState<string | null>(null);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [hasUnreadMessages, setHasUnreadMessages] = useState(false);

  // Poll for unread support messages
  useEffect(() => {
    if (!isAuthenticated) return;

    let cancelled = false;

    const fetchUnread = async () => {
      try {
        const supabase = getSupabaseClient();

        // Check support conversation unread count
        const { data: supportConvo } = await supabase
          .from('support_conversations')
          .select('user_unread_count')
          .maybeSingle();

        // Check seeker message notification counts
        const { data: notifications } = await supabase
          .from('message_notifications')
          .select('count');

        if (cancelled) return;

        const supportUnread = supportConvo?.user_unread_count ?? 0;
        const seekerUnread = notifications?.reduce((sum: number, n: any) => sum + (n.count || 0), 0) ?? 0;

        setHasUnreadMessages(supportUnread > 0 || seekerUnread > 0);
      } catch {
        // ignore
      }
    };

    fetchUnread();
    const intervalId = setInterval(fetchUnread, 30000);

    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
  }, [isAuthenticated, isChatDrawerOpen]);

  // Chameleon identification for active guides
  useEffect(() => {
    const doIdentify = async () => {
      const supabase = getSupabaseClient();
      const { data: auth } = await supabase.auth.getUser();
      if (!auth?.user) return;

      // Fetch guide status
      const { data: guide, error: guideError } = await supabase
        .from('guides')
        .select('status')
        .eq('user_id', auth.user.id)
        .single();

      if (guideError || !guide) return;

      // Only fire for ACTIVE guides
      if (guide.status !== 'active') return;

      // Fetch profile data separately
      const { data: profile } = await supabase
        .from('profiles')
        .select('email, full_name')
        .eq('id', auth.user.id)
        .single();

      const env = (window as any).__ENV__ || {};

      identifyGuide({
        id: auth.user.id,
        email: profile?.email ?? auth.user.email ?? null,
        fullName: profile?.full_name ?? null,
        appStatus: guide.status,
        environment: env.ENVIRONMENT,
      });
    };

    doIdentify();
  }, []);

  // Revalidate auth session when user changes tabs
  useEffect(() => {
    if (isAuthenticated) {
      revalidateSession();
    }
  }, [activeTab, revalidateSession, isAuthenticated]);

  // Track settings sub-tab (calendar or payments)
  const [settingsSubTab, setSettingsSubTab] = useState<'calendar' | 'payments'>('calendar');

  // Handle URL hash for tab selection
  // Supports formats: #settings, #settings-payments, #settings-calendar
  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash.slice(1); // Remove the '#'

      // Check for settings sub-tab format (e.g., #settings-payments)
      if (hash.startsWith('settings-')) {
        const subTab = hash.replace('settings-', '');
        if (subTab === 'payments' || subTab === 'calendar') {
          setActiveTab('settings');
          setSettingsSubTab(subTab);
          return;
        }
      }

      if (hash && ['hub', 'seekers', 'schedule', 'payments', 'packages', 'resources', 'profile', 'settings'].includes(hash)) {
        setActiveTab(hash);
        // Reset settings sub-tab to default when navigating to settings without sub-tab
        if (hash === 'settings') {
          setSettingsSubTab('calendar');
        }
      } else if (!hash) {
        // Set default hash if none exists
        window.location.hash = 'hub';
      }
    };

    // Set initial tab from hash
    handleHashChange();

    // Listen for hash changes (browser back/forward)
    window.addEventListener('hashchange', handleHashChange);

    return () => {
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, []);

  const handleLogout = async () => {
    const supabase = getSupabaseClient();

    // Clear all cached data to prevent leaking across sessions
    localStorage.clear();
    sessionStorage.clear();
    await supabase.auth.signOut();
    navigate('/login');
  };

  const ensureShareLink = useCallback(async (force?: boolean) => {
    if (!force && shareLink) {
      return;
    }

    const env = (window as any).__ENV__;
    const baseUrlRaw = env?.SEEKER_BASE_SHARE_URL;
    if (!baseUrlRaw) {
      setShareLinkError('Share URL is not configured.');
      setShareLink(null);
      return;
    }

    if (force) {
      setShareLink(null);
    }

    const normalizedBase = baseUrlRaw.replace(/\/+$/, '');

    setShareLinkLoading(true);
    setShareLinkError(null);

    try {
      const supabase = getSupabaseClient();
      const {
        data: { user },
        error: userError,
      } = await supabase.auth.getUser();

      if (userError) {
        throw userError;
      }

      if (!user) {
        throw new Error('You must be signed in to share your link.');
      }

      const guideId = user.id;

      const { data: existingLink, error: existingError } = await supabase
        .from('guide_signup_links')
        .select('identifier')
        .eq('guide_id', guideId)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (existingError) {
        throw existingError;
      }

      let identifier = existingLink?.identifier ?? null;

      if (!identifier) {
        const { data: insertedLink, error: insertError } = await supabase
          .from('guide_signup_links')
          .insert({ guide_id: guideId })
          .select('identifier')
          .single();

        if (insertError) {
          throw insertError;
        }

        identifier = insertedLink?.identifier ?? null;
      }

      if (!identifier) {
        throw new Error('Unable to generate your invite link.');
      }

      setShareLink(`${normalizedBase}/${identifier}`);
    } catch (err) {
      console.error('Failed to prepare share link', err);
      const message = err instanceof Error ? err.message : 'Failed to generate invite link.';
      setShareLinkError(message);
      setShareLink(null);
    } finally {
      setShareLinkLoading(false);
    }
  }, [shareLink]);

  const handleShareInviteClick = useCallback(() => {
    setShareLinkError(null);
    setIsShareModalOpen(true);
    void ensureShareLink();
  }, [ensureShareLink]);

  const handleRetryShareLink = useCallback(() => {
    void ensureShareLink(true);
  }, [ensureShareLink]);

  const handleCloseShareModal = useCallback(() => {
    setIsShareModalOpen(false);
  }, []);

  const handleRequestReviewClick = useCallback(() => {
    setIsRequestReviewModalOpen(true);
  }, []);

  const handleCloseRequestReviewModal = useCallback(() => {
    setIsRequestReviewModalOpen(false);
  }, []);

  const handleViewSeekerDetails = (seeker: any = null) => {
    if (seeker) {
      setSelectedSeeker(seeker);
      setIsSeekerDetailModalOpen(true);
    } else {
      setSelectedSeeker(null);
      setIsSeekerDetailModalOpen(false);
    }
  };

  const handleDataChange = useCallback(() => {
    setContentRefreshKey((prev) => prev + 1);
  }, []);

  const handleChatClick = () => {
    setChatSeekerId(null);
    setIsChatDrawerOpen(true);
  };

  const handleOpenChatWithSeeker = useCallback((seekerId: string) => {
    setChatSeekerId(seekerId);
    setIsChatDrawerOpen(true);
  }, []);

  const handleCloseChatDrawer = () => {
    setIsChatDrawerOpen(false);
    setChatSeekerId(null);
  };

  const renderContent = () => {
    switch (activeTab) {
      case 'hub':
        return <HubPage key={contentRefreshKey} onViewDetails={handleViewSeekerDetails} onOpenChat={handleOpenChatWithSeeker} />;
      case 'seekers':
        return <SeekersPage key={contentRefreshKey} onViewDetails={handleViewSeekerDetails} />;
      case 'schedule':
        return <SchedulePage key={contentRefreshKey} />;
      case 'payments':
        return <PaymentsPage key={contentRefreshKey} />;
      case 'packages':
        return <PackagesPage key={contentRefreshKey} />;
      case 'resources':
        return <ResourcesPage key={contentRefreshKey} />;
      case 'profile':
        return <ProfilePage key={contentRefreshKey} />;
      case 'settings':
        return <SettingsPage key={contentRefreshKey} initialTab={settingsSubTab} />;
      default:
        return <HubPage key={contentRefreshKey} onViewDetails={handleViewSeekerDetails} />;
    }
  };

  // Show loading state while checking auth
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FFFCEF]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2C2731] mx-auto mb-4"></div>
          <p className="text-[#2C2731]/70 text-sm">Loading...</p>
        </div>
      </div>
    );
  }

  // Don't render dashboard if not authenticated (redirect will happen)
  if (!isAuthenticated) {
    return null;
  }

  return (
    <div className="min-h-screen flex flex-col bg-[#FFFCEF] text-[#2C2731]">

      <Header
        isLoggedIn={true}
        userProfile={userProfile}
        onLogout={handleLogout}
        onShareInviteClick={handleShareInviteClick}
        onRequestReviewClick={handleRequestReviewClick}
        onChatClick={handleChatClick}
        hasUnreadMessages={hasUnreadMessages}
      />



      <div className="flex flex-1">

        <aside className={`bg-[#FFFCEF] transition-all duration-300 ease-in-out ${
          isSidebarCollapsed ? 'w-16' : 'w-64'
        }`}>
          <nav className="p-4 space-y-2">
            <button
              onClick={() => window.location.hash = 'hub'}
              className={`flex items-center gap-2 p-2 rounded w-full text-left transition-colors ${
                activeTab === 'hub' ? 'bg-[#2C2731] text-[#FFFCEF]' : 'text-[#2C2731] hover:bg-[#E8DFBD]'
              } ${isSidebarCollapsed ? 'justify-center' : ''}`}
              title={isSidebarCollapsed ? 'Hub' : ''}
            >
              <div className="relative flex-shrink-0">
                <HomeIcon className="h-5 w-5" />
                {hasUnreadMessages && (
                  <span className="absolute -top-1 -right-1 flex h-2.5 w-2.5 rounded-full bg-red-500" />
                )}
              </div>
              {!isSidebarCollapsed && <span>Hub</span>}
            </button>
            <button
              onClick={() => window.location.hash = 'seekers'}
              className={`flex items-center gap-2 p-2 rounded w-full text-left transition-colors ${
                activeTab === 'seekers' ? 'bg-[#2C2731] text-[#FFFCEF]' : 'text-[#2C2731] hover:bg-[#E8DFBD]'
              } ${isSidebarCollapsed ? 'justify-center' : ''}`}
              title={isSidebarCollapsed ? 'Seekers' : ''}
            >
              <div className="relative flex-shrink-0">
                <UsersIcon className="h-5 w-5" />
                {hasUnreadMessages && (
                  <span className="absolute -top-1 -right-1 flex h-2.5 w-2.5 rounded-full bg-red-500" />
                )}
              </div>
              {!isSidebarCollapsed && <span>Seekers</span>}
            </button>
            <button
              onClick={() => window.location.hash = 'schedule'}
              className={`flex items-center gap-2 p-2 rounded w-full text-left transition-colors ${
                activeTab === 'schedule' ? 'bg-[#2C2731] text-[#FFFCEF]' : 'text-[#2C2731] hover:bg-[#E8DFBD]'
              } ${isSidebarCollapsed ? 'justify-center' : ''}`}
              title={isSidebarCollapsed ? 'Schedule' : ''}
            >
              <CalendarIcon className="h-5 w-5 flex-shrink-0" />
              {!isSidebarCollapsed && <span>Schedule</span>}
            </button>
            <button
              onClick={() => window.location.hash = 'payments'}
              className={`flex items-center gap-2 p-2 rounded w-full text-left transition-colors ${
                activeTab === 'payments' ? 'bg-[#2C2731] text-[#FFFCEF]' : 'text-[#2C2731] hover:bg-[#E8DFBD]'
              } ${isSidebarCollapsed ? 'justify-center' : ''}`}
              title={isSidebarCollapsed ? 'Payments' : ''}
            >
              <CreditCardIcon className="h-5 w-5 flex-shrink-0" />
              {!isSidebarCollapsed && <span>Payments</span>}
            </button>
            <button
              onClick={() => window.location.hash = 'packages'}
              className={`flex items-center gap-2 p-2 rounded w-full text-left transition-colors ${
                activeTab === 'packages' ? 'bg-[#2C2731] text-[#FFFCEF]' : 'text-[#2C2731] hover:bg-[#E8DFBD]'
              } ${isSidebarCollapsed ? 'justify-center' : ''}`}
              title={isSidebarCollapsed ? 'Packages' : ''}
            >
              <ArchiveBoxIcon className="h-5 w-5 flex-shrink-0" />
              {!isSidebarCollapsed && <span>Packages</span>}
            </button>
            <button
              onClick={() => window.location.hash = 'resources'}
              className={`flex items-center gap-2 p-2 rounded w-full text-left transition-colors ${
                activeTab === 'resources' ? 'bg-[#2C2731] text-[#FFFCEF]' : 'text-[#2C2731] hover:bg-[#E8DFBD]'
              } ${isSidebarCollapsed ? 'justify-center' : ''}`}
              title={isSidebarCollapsed ? 'Resources' : ''}
            >
              <BookOpenIcon className="h-5 w-5 flex-shrink-0" />
              {!isSidebarCollapsed && <span>Resources</span>}
            </button>
            <button
              onClick={() => window.location.hash = 'profile'}
              className={`flex items-center gap-2 p-2 rounded w-full text-left transition-colors ${
                activeTab === 'profile' ? 'bg-[#2C2731] text-[#FFFCEF]' : 'text-[#2C2731] hover:bg-[#E8DFBD]'
              } ${isSidebarCollapsed ? 'justify-center' : ''}`}
              title={isSidebarCollapsed ? 'Profile' : ''}
            >
              <UserIcon className="h-5 w-5 flex-shrink-0" />
              {!isSidebarCollapsed && <span>Profile</span>}
            </button>
            <button
              onClick={() => window.location.hash = 'settings'}
              className={`flex items-center gap-2 p-2 rounded w-full text-left transition-colors ${
                activeTab === 'settings' ? 'bg-[#2C2731] text-[#FFFCEF]' : 'text-[#2C2731] hover:bg-[#E8DFBD]'
              } ${isSidebarCollapsed ? 'justify-center' : ''}`}
              title={isSidebarCollapsed ? 'Settings' : ''}
            >
              <Cog6ToothIcon className="h-5 w-5 flex-shrink-0" />
              {!isSidebarCollapsed && <span>Settings</span>}
            </button>

            {/* Collapse/Expand Toggle */}
            <div className="pt-4 border-t border-[#E8DFBD]">
              <button
                onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
                className="flex items-center gap-2 p-2 rounded w-full text-left transition-colors text-[#2C2731] hover:bg-[#E8DFBD]"
                title={isSidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              >
                {isSidebarCollapsed ? (
                  <ChevronRightIcon className="h-5 w-5 mx-auto" />
                ) : (
                  <>
                    <ChevronLeftIcon className="h-5 w-5 flex-shrink-0" />
                    <span>Collapse</span>
                  </>
                )}
              </button>
            </div>
          </nav>
        </aside>

        {/* Content Area */}
        <main className="flex-1 bg-[#FFFCEF]">
          {renderContent()}
        </main>
      </div>

      {/* Share Invite Link Modal */}
      <ShareInviteLinkModal
        isOpen={isShareModalOpen}
        onClose={handleCloseShareModal}
        shareLink={shareLink}
        loading={shareLinkLoading}
        error={shareLinkError}
        onRetry={handleRetryShareLink}
      />

      {/* Request Off-Platform Review Modal */}
      <RequestOffPlatformReviewModal
        isOpen={isRequestReviewModalOpen}
        onClose={handleCloseRequestReviewModal}
      />

      {/* Seeker Detail Modal */}
      <SeekerDetailModal
        isOpen={isSeekerDetailModalOpen}
        onClose={() => handleViewSeekerDetails(null)}
        seeker={selectedSeeker}
        onDataChange={handleDataChange}
      />

      {/* Chat Drawer */}
      <ChatDrawer
        isOpen={isChatDrawerOpen}
        onClose={handleCloseChatDrawer}
        initialSeekerId={chatSeekerId}
      />
    </div>
  );
}
